import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { FlowEntryBanner } from '../../components/workflow/FlowEntryBanner';
import { legacyApi, Customer } from '../../services/legacyApi';
import { api } from '../../services/api';
import type { Account, ApOpenBill, ArOpenInvoice, ApPdcMode, ArPdcMode, ArReceiptRecord, PdcAllocationLine, PdcInstrumentSummary } from '../../services/types';
import { toast } from 'sonner';
import { RequiredLabel } from '../../components/forms/RequiredLabel';
import { FieldErrorText } from '../../components/forms/FieldErrorText';
import { showValidationSummaryToast } from '../../components/forms/ValidationSummaryToast';
import { focusFirstInvalidField, validateRequiredFields } from '../../components/forms/formValidationUtils';

type Side = 'AR' | 'AP';
type Mode = ArPdcMode | ApPdcMode;
type AllocationBadge = 'FULLY_ALLOCATED' | 'PARTIALLY_ALLOCATED' | 'UNALLOCATED';

const MODE_LABELS: Record<string, string> = {
    INVOICE_SETTLEMENT: 'Invoice Settlement',
    CUSTOMER_PREPAYMENT: 'Customer Prepayment',
    BILL_SETTLEMENT: 'Bill Settlement',
    SUPPLIER_PREPAYMENT: 'Supplier Prepayment',
};

const STATUS_LABELS: Record<number, string> = { 1: 'Registered', 2: 'Deposited', 3: 'Cleared', 4: 'Bounced', 5: 'Cancelled' };
const ALLOCATION_BADGE_LABELS: Record<AllocationBadge, string> = {
    FULLY_ALLOCATED: 'Fully Allocated',
    PARTIALLY_ALLOCATED: 'Partially Allocated',
    UNALLOCATED: 'Unallocated',
};

interface ReceiptAllocationMetric {
    amount: number;
    applied: number;
    unapplied: number;
    badge: AllocationBadge;
}

function isSettlementMode(side: Side, mode: string): boolean {
    return (side === 'AR' && mode === 'INVOICE_SETTLEMENT') || (side === 'AP' && mode === 'BILL_SETTLEMENT');
}

function isClearedCustomerPrepayment(row: PdcInstrumentSummary): boolean {
    return String(row.side || '').toUpperCase() === 'AR' && String(row.mode || '').toUpperCase() === 'CUSTOMER_PREPAYMENT' && Number(row.status_id || 0) === 3;
}

function deriveReceiptAllocationMetric(receipt: ArReceiptRecord): ReceiptAllocationMetric {
    const amount = Number(receipt.amount || 0);
    const applied = Number(receipt.applied_amount || 0);
    const unapplied = Number(receipt.unapplied_amount || 0);
    let badge: AllocationBadge = 'UNALLOCATED';
    if (applied > 0.01 && unapplied > 0.01) {
        badge = 'PARTIALLY_ALLOCATED';
    } else if (applied > 0.01 && unapplied <= 0.01) {
        badge = 'FULLY_ALLOCATED';
    }
    return { amount, applied, unapplied, badge };
}

function allocationBadgeClasses(badge: AllocationBadge): string {
    if (badge === 'FULLY_ALLOCATED') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    if (badge === 'PARTIALLY_ALLOCATED') return 'bg-amber-100 text-amber-800 border-amber-200';
    return 'bg-slate-100 text-slate-700 border-slate-200';
}

function isPostableCashBankAccount(account: Account) {
    const label = `${account.code || ''} ${account.name || ''}`.toLowerCase();
    return (
        account.type === 'Asset'
        && account.is_active !== false
        && account.is_postable !== false
        && (label.includes('cash') || label.includes('bank') || account.code === '1000' || account.parent_account_id === 1000)
    );
}

export function PDCRegister() {
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [suppliers, setSuppliers] = useState<any[]>([]);
    const [side, setSide] = useState<Side>('AR');
    const [partyId, setPartyId] = useState('');
    const [mode, setMode] = useState<Mode>('INVOICE_SETTLEMENT');
    const [amount, setAmount] = useState<number>(0);
    const [instrumentNo, setInstrumentNo] = useState('');
    const [instrumentDate, setInstrumentDate] = useState(new Date().toISOString().slice(0, 10));
    const [dueDate, setDueDate] = useState(new Date().toISOString().slice(0, 10));
    const [bankName, setBankName] = useState('');
    const [reference, setReference] = useState('');
    const [allocDraft, setAllocDraft] = useState<Record<number, string>>({});
    const [openInvoices, setOpenInvoices] = useState<ArOpenInvoice[]>([]);
    const [openBills, setOpenBills] = useState<ApOpenBill[]>([]);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [cashAccounts, setCashAccounts] = useState<Account[]>([]);
    const [clearDialogPdc, setClearDialogPdc] = useState<PdcInstrumentSummary | null>(null);
    const [clearCashAccountId, setClearCashAccountId] = useState('');
    const [clearanceDate, setClearanceDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [editingPdcId, setEditingPdcId] = useState<number | null>(null);

    const [rows, setRows] = useState<PdcInstrumentSummary[]>([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [allocationBadgeFilter, setAllocationBadgeFilter] = useState<'ALL' | AllocationBadge>('ALL');
    const [appliedOnly, setAppliedOnly] = useState(false);
    const [unappliedOnly, setUnappliedOnly] = useState(false);
    const [listLoading, setListLoading] = useState(false);
    const [actionBusy, setActionBusy] = useState<Record<number, string>>({});
    const [receiptMetrics, setReceiptMetrics] = useState<Record<number, ReceiptAllocationMetric>>({});
    const [allocatingPdc, setAllocatingPdc] = useState<PdcInstrumentSummary | null>(null);
    const [allocationReceipt, setAllocationReceipt] = useState<ArReceiptRecord | null>(null);
    const [allocationOpenInvoices, setAllocationOpenInvoices] = useState<ArOpenInvoice[]>([]);
    const [allocationDraft, setAllocationDraft] = useState<Record<number, string>>({});
    const [allocationLoading, setAllocationLoading] = useState(false);
    const [allocationSaving, setAllocationSaving] = useState(false);

    const partyOptions = side === 'AR' ? customers : suppliers;
    const modeOptions = side === 'AR' ? (['INVOICE_SETTLEMENT', 'CUSTOMER_PREPAYMENT'] as Mode[]) : (['BILL_SETTLEMENT', 'SUPPLIER_PREPAYMENT'] as Mode[]);

    useEffect(() => {
        legacyApi.customers.list().then(setCustomers).catch(() => setCustomers([]));
        legacyApi.suppliers.list().then(setSuppliers).catch(() => setSuppliers([]));
        api.financials
            .listAccounts()
            .then((accounts) => setCashAccounts(accounts.filter(isPostableCashBankAccount)))
            .catch(() => setCashAccounts([]));
    }, []);

    useEffect(() => {
        setPartyId('');
        setMode(side === 'AR' ? 'INVOICE_SETTLEMENT' : 'BILL_SETTLEMENT');
        setAllocDraft({});
        setEditingPdcId(null);
    }, [side]);

    useEffect(() => {
        if (!partyId || !isSettlementMode(side, mode)) {
            setOpenInvoices([]);
            setOpenBills([]);
            if (!editingPdcId) setAllocDraft({});
            return;
        }
        if (side === 'AR') {
            api.financials.listArOpenInvoices(Number(partyId)).then(setOpenInvoices).catch(() => setOpenInvoices([]));
            setOpenBills([]);
        } else {
            api.financials.listApOpenBills(Number(partyId)).then(setOpenBills).catch(() => setOpenBills([]));
            setOpenInvoices([]);
        }
        if (!editingPdcId) setAllocDraft({});
    }, [partyId, side, mode, editingPdcId]);

    const allocationLines = useMemo<PdcAllocationLine[]>(() => {
        const sourceIds = side === 'AR' ? openInvoices.map((r) => r.invoice_id) : openBills.map((r) => r.bill_id);
        return sourceIds
            .map((id) => {
                const value = parseFloat(allocDraft[id] ?? '');
                if (!Number.isFinite(value) || value <= 0) return null;
                return { document_id: id, apply_amount: Number(value.toFixed(2)) };
            })
            .filter((row): row is PdcAllocationLine => !!row);
    }, [side, openInvoices, openBills, allocDraft]);

    const allocationTotal = useMemo(() => Number(allocationLines.reduce((s, x) => s + x.apply_amount, 0).toFixed(2)), [allocationLines]);
    const prepaymentAllocationLines = useMemo<PdcAllocationLine[]>(() => {
        return allocationOpenInvoices
            .map((row) => {
                const value = parseFloat(allocationDraft[row.invoice_id] ?? '');
                if (!Number.isFinite(value) || value <= 0) return null;
                return { document_id: row.invoice_id, apply_amount: Number(value.toFixed(2)) };
            })
            .filter((row): row is PdcAllocationLine => !!row);
    }, [allocationOpenInvoices, allocationDraft]);
    const prepaymentAllocationTotal = useMemo(
        () => Number(prepaymentAllocationLines.reduce((sum, line) => sum + line.apply_amount, 0).toFixed(2)),
        [prepaymentAllocationLines]
    );

    const getRowReceiptMetric = (row: PdcInstrumentSummary): ReceiptAllocationMetric | null => {
        if (!isClearedCustomerPrepayment(row)) return null;
        const receiptId = Number(row.cleared_receipt_id || 0);
        if (receiptId <= 0) return null;
        const cached = receiptMetrics[receiptId];
        if (cached) return cached;
        return {
            amount: Number(row.amount || 0),
            applied: 0,
            unapplied: Number(row.amount || 0),
            badge: 'UNALLOCATED',
        };
    };

    const visibleRows = useMemo(() => {
        return rows.filter((row) => {
            const metric = getRowReceiptMetric(row);
            if (allocationBadgeFilter !== 'ALL') {
                if (!metric || metric.badge !== allocationBadgeFilter) return false;
            }
            if (appliedOnly) {
                if (!metric || metric.applied <= 0.01) return false;
            }
            if (unappliedOnly) {
                if (!metric || metric.unapplied <= 0.01) return false;
            }
            return true;
        });
    }, [rows, allocationBadgeFilter, appliedOnly, unappliedOnly, receiptMetrics]);

    const loadRows = async () => {
        setListLoading(true);
        try {
            const data = await api.financials.listPdc({ side, status_id: statusFilter ? Number(statusFilter) : undefined, include_closed: true });
            setRows(data.items || []);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load PDC register');
        } finally {
            setListLoading(false);
        }
    };

    useEffect(() => {
        loadRows();
    }, [side, statusFilter]);

    useEffect(() => {
        const targetReceiptIds = rows
            .filter((row) => isClearedCustomerPrepayment(row) && Number(row.cleared_receipt_id || 0) > 0)
            .map((row) => Number(row.cleared_receipt_id || 0));
        const missingIds = targetReceiptIds.filter((id) => !receiptMetrics[id]);
        if (missingIds.length === 0) return;

        let cancelled = false;
        Promise.all(
            missingIds.map(async (receiptId) => {
                try {
                    const receipt = await api.financials.getArReceipt(receiptId);
                    return { receiptId, metric: deriveReceiptAllocationMetric(receipt) };
                } catch {
                    return null;
                }
            })
        ).then((results) => {
            if (cancelled) return;
            setReceiptMetrics((prev) => {
                const next = { ...prev };
                results.forEach((entry) => {
                    if (entry) next[entry.receiptId] = entry.metric;
                });
                return next;
            });
        });

        return () => {
            cancelled = true;
        };
    }, [rows, receiptMetrics]);

    const handleRegisterOrUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        const validation = validateRequiredFields([
            { key: 'partyId', label: side === 'AR' ? 'Customer' : 'Supplier', value: partyId, required: true },
            { key: 'mode', label: 'Mode', value: mode, required: true },
            { key: 'amount', label: 'Amount', value: amount > 0 ? amount : '', required: true },
            { key: 'instrumentNo', label: 'Instrument No', value: instrumentNo, required: !editingPdcId },
            { key: 'instrumentDate', label: 'Instrument Date', value: instrumentDate, required: !editingPdcId },
            { key: 'dueDate', label: 'Due Date', value: dueDate, required: !editingPdcId },
        ]);
        const nextErrors = { ...validation.errorsByField } as Record<string, string>;
        if (isSettlementMode(side, mode) && (allocationLines.length === 0 || Math.abs(allocationTotal - amount) > 0.01)) {
            nextErrors.allocations = 'Settlement allocations must exactly match amount.';
        }
        if (!validation.valid || Object.keys(nextErrors).length > 0) {
            setFieldErrors(nextErrors);
            showValidationSummaryToast();
            focusFirstInvalidField(validation.firstInvalidField || 'allocations');
            return;
        }
        setFieldErrors({});

        setLoading(true);
        try {
            if (editingPdcId) {
                await api.financials.updatePdcAllocations(editingPdcId, allocationLines);
                toast.success(`PDC #${editingPdcId} allocations updated.`);
            } else if (side === 'AR') {
                await api.financials.registerArPdc({
                    customer_id: Number(partyId),
                    mode,
                    amount,
                    instrument_no: instrumentNo,
                    instrument_date: instrumentDate,
                    due_date: dueDate,
                    bank_name: bankName || undefined,
                    reference: reference || undefined,
                    allocations: isSettlementMode(side, mode) ? allocationLines : [],
                });
                toast.success('AR PDC registered.');
            } else {
                await api.financials.registerApPdc({
                    supplier_id: Number(partyId),
                    mode,
                    amount,
                    instrument_no: instrumentNo,
                    instrument_date: instrumentDate,
                    due_date: dueDate,
                    bank_name: bankName || undefined,
                    reference: reference || undefined,
                    allocations: isSettlementMode(side, mode) ? allocationLines : [],
                });
                toast.success('AP PDC registered.');
            }
            setEditingPdcId(null);
            setAmount(0);
            setInstrumentNo('');
            setReference('');
            setAllocDraft({});
            await loadRows();
        } catch (error: any) {
            toast.error(error?.message || 'Failed to save PDC');
        } finally {
            setLoading(false);
        }
    };

    const runAction = async (pdcId: number, action: 'deposit' | 'clear' | 'bounce' | 'cancel') => {
        setActionBusy((prev) => ({ ...prev, [pdcId]: action }));
        try {
            await api.financials.transitionPdc({
                pdc_id: pdcId,
                action,
                reason: `${action} via register`,
            });
            toast.success(`PDC ${action} completed.`);
            await loadRows();
        } catch (error: any) {
            toast.error(error?.message || `Failed to ${action} PDC`);
        } finally {
            setActionBusy((prev) => ({ ...prev, [pdcId]: '' }));
        }
    };

    const openClearDialog = (row: PdcInstrumentSummary) => {
        setClearDialogPdc(row);
        setClearCashAccountId('');
        setClearanceDate('');
    };

    const confirmClearPdc = async () => {
        if (!clearDialogPdc) return;
        if (!clearCashAccountId) {
            toast.error('Select the Cash/Bank account before clearing this PDC');
            return;
        }
        if (!clearanceDate) {
            toast.error('Select the actual clearance date before clearing this PDC');
            return;
        }
        const pdcId = Number(clearDialogPdc.pdc_id);
        setActionBusy((prev) => ({ ...prev, [pdcId]: 'clear' }));
        try {
            await api.financials.transitionPdc({
                pdc_id: pdcId,
                action: 'clear',
                action_date: `${clearanceDate}T00:00:00`,
                reason: 'clear via register',
                cash_account_id: Number(clearCashAccountId),
            });
            toast.success('PDC clear completed.');
            setClearDialogPdc(null);
            setClearCashAccountId('');
            setClearanceDate('');
            await loadRows();
        } catch (error: any) {
            toast.error(error?.message || 'Failed to clear PDC');
        } finally {
            setActionBusy((prev) => ({ ...prev, [pdcId]: '' }));
        }
    };

    const resetPrepaymentAllocation = () => {
        setAllocatingPdc(null);
        setAllocationReceipt(null);
        setAllocationOpenInvoices([]);
        setAllocationDraft({});
        setAllocationLoading(false);
        setAllocationSaving(false);
    };

    const autoAllocatePrepayment = () => {
        const unapplied = Number(allocationReceipt?.unapplied_amount || 0);
        let remaining = Number.isFinite(unapplied) ? unapplied : 0;
        if (remaining <= 0) return;
        const next: Record<number, string> = {};
        allocationOpenInvoices.forEach((invoice) => {
            if (remaining <= 0) return;
            const apply = Math.min(Number(invoice.outstanding_amount || 0), remaining);
            if (apply > 0) {
                next[invoice.invoice_id] = apply.toFixed(2);
                remaining = Number((remaining - apply).toFixed(2));
            }
        });
        setAllocationDraft(next);
    };

    const beginPrepaymentAllocation = async (row: PdcInstrumentSummary) => {
        if (!isClearedCustomerPrepayment(row)) return;
        const receiptId = Number(row.cleared_receipt_id || 0);
        if (receiptId <= 0) {
            toast.error('Cleared receipt id is missing for this PDC.');
            return;
        }
        setAllocationLoading(true);
        try {
            const [receipt, open] = await Promise.all([
                api.financials.getArReceipt(receiptId),
                api.financials.listArOpenInvoices(Number(row.party_id || 0)),
            ]);
            setReceiptMetrics((prev) => ({ ...prev, [receipt.receipt_id]: deriveReceiptAllocationMetric(receipt) }));
            setAllocatingPdc(row);
            setAllocationReceipt(receipt);
            setAllocationOpenInvoices(open || []);
            const nextDraft: Record<number, string> = {};
            let remaining = Number(receipt.unapplied_amount || 0);
            (open || []).forEach((invoice) => {
                if (remaining <= 0) return;
                const apply = Math.min(Number(invoice.outstanding_amount || 0), remaining);
                if (apply > 0) {
                    nextDraft[invoice.invoice_id] = apply.toFixed(2);
                    remaining = Number((remaining - apply).toFixed(2));
                }
            });
            setAllocationDraft(nextDraft);
            if ((open || []).length === 0) {
                toast.info('No open invoices found for this customer.');
            } else if (Number(receipt.unapplied_amount || 0) <= 0.01) {
                toast.info('This prepayment receipt is already fully allocated.');
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load prepayment allocation details');
        } finally {
            setAllocationLoading(false);
        }
    };

    const submitPrepaymentAllocation = async () => {
        if (!allocationReceipt || !allocatingPdc) return;
        const unapplied = Number(allocationReceipt.unapplied_amount || 0);
        if (prepaymentAllocationLines.length === 0) {
            toast.error('Enter at least one invoice allocation amount.');
            return;
        }
        if (prepaymentAllocationTotal > unapplied + 0.01) {
            toast.error(`Allocation exceeds unapplied balance (${unapplied.toFixed(2)}).`);
            return;
        }
        setAllocationSaving(true);
        try {
            const updated = await api.financials.allocateArReceipt(allocationReceipt.receipt_id, prepaymentAllocationLines);
            const remaining = Number(updated.unapplied_amount || 0);
            setReceiptMetrics((prev) => ({ ...prev, [updated.receipt_id]: deriveReceiptAllocationMetric(updated) }));
            setAllocationReceipt(updated);
            setAllocationDraft({});
            toast.success(`Allocated ${prepaymentAllocationTotal.toFixed(2)} from receipt #${updated.receipt_id}.`);
            await loadRows();
            if (remaining <= 0.01) {
                toast.info('Prepayment fully allocated.');
                resetPrepaymentAllocation();
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to allocate prepayment receipt');
        } finally {
            setAllocationSaving(false);
        }
    };

    const loadForAllocationEdit = async (row: PdcInstrumentSummary) => {
        try {
            const detail = await api.financials.getPdc(row.pdc_id);
            setSide(String(detail.side || 'AR').toUpperCase() === 'AP' ? 'AP' : 'AR');
            setPartyId(String(detail.party_id || ''));
            setMode(detail.mode as Mode);
            setAmount(Number(detail.amount || 0));
            setEditingPdcId(detail.pdc_id);
            const draft: Record<number, string> = {};
            (detail.allocations || []).forEach((line) => {
                draft[line.document_id] = Number(line.apply_amount || 0).toFixed(2);
            });
            setAllocDraft(draft);
            toast.info(`Loaded PDC #${detail.pdc_id} allocations into editor.`);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load PDC detail');
        }
    };

    return (
        <div className="space-y-4">
            <FlowEntryBanner title="PDC Register" pathLabel="Deferred Settlement Lifecycle" nextHint="Register now, post accounting on clearance." helpModule="finance" />
            <Card>
                <CardHeader><CardTitle>{editingPdcId ? `Edit Allocations for PDC #${editingPdcId}` : 'Register PDC'}</CardTitle></CardHeader>
                <CardContent>
                    <form onSubmit={handleRegisterOrUpdate} className="space-y-3">
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                            <div><RequiredLabel required htmlFor="side">Side</RequiredLabel><select id="side" className="w-full rounded border p-2" value={side} onChange={(e) => setSide(e.target.value as Side)}><option value="AR">AR Received</option><option value="AP">AP Issued</option></select></div>
                            <div><RequiredLabel required htmlFor="party">Party</RequiredLabel><select id="party" className="w-full rounded border p-2" value={partyId} onChange={(e) => setPartyId(e.target.value)}><option value="">Select</option>{partyOptions.map((row: any) => (<option key={side === 'AR' ? row.customer_id : row.supplier_id} value={side === 'AR' ? row.customer_id : row.supplier_id}>{side === 'AR' ? row.customer_name : row.supplier_name}</option>))}</select><FieldErrorText message={fieldErrors.partyId} /></div>
                            <div><RequiredLabel required htmlFor="mode">Mode</RequiredLabel><select id="mode" className="w-full rounded border p-2" value={mode} onChange={(e) => setMode(e.target.value as Mode)}>{modeOptions.map((m) => (<option key={m} value={m}>{MODE_LABELS[m]}</option>))}</select></div>
                        </div>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                            <div><RequiredLabel required htmlFor="amount">Amount</RequiredLabel><input id="amount" type="number" min="0.01" step="0.01" className="w-full rounded border p-2" value={amount} onChange={(e) => setAmount(Number(e.target.value || 0))} /><FieldErrorText message={fieldErrors.amount} /></div>
                            <div><RequiredLabel required={!editingPdcId} htmlFor="instrumentNo">Instrument No</RequiredLabel><input id="instrumentNo" className="w-full rounded border p-2" value={instrumentNo} onChange={(e) => setInstrumentNo(e.target.value)} disabled={!!editingPdcId} /><FieldErrorText message={fieldErrors.instrumentNo} /></div>
                            <div><RequiredLabel required={!editingPdcId} htmlFor="instrumentDate">Instrument Date</RequiredLabel><input id="instrumentDate" type="date" className="w-full rounded border p-2" value={instrumentDate} onChange={(e) => setInstrumentDate(e.target.value)} disabled={!!editingPdcId} /></div>
                            <div><RequiredLabel required={!editingPdcId} htmlFor="dueDate">Due Date</RequiredLabel><input id="dueDate" type="date" className="w-full rounded border p-2" value={dueDate} onChange={(e) => setDueDate(e.target.value)} disabled={!!editingPdcId} /></div>
                        </div>
                        {!editingPdcId && <div className="grid grid-cols-1 gap-3 md:grid-cols-2"><input className="w-full rounded border p-2" placeholder="Bank Name" value={bankName} onChange={(e) => setBankName(e.target.value)} /><input className="w-full rounded border p-2" placeholder="Reference" value={reference} onChange={(e) => setReference(e.target.value)} /></div>}
                        {isSettlementMode(side, mode) && (
                            <div className="rounded border p-2">
                                <p className="mb-2 text-sm font-medium">Allocations</p>
                                <div className="max-h-52 overflow-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-muted/30"><tr><th className="p-2 text-left">{side === 'AR' ? 'Invoice' : 'Bill'}</th><th className="p-2 text-right">Outstanding</th><th className="p-2 text-right">Apply</th></tr></thead>
                                        <tbody>
                                            {(side === 'AR' ? openInvoices.map((x) => ({ id: x.invoice_id, label: x.invoice_no, outstanding: x.outstanding_amount })) : openBills.map((x) => ({ id: x.bill_id, label: x.bill_no, outstanding: x.outstanding_amount }))).map((row) => (
                                                <tr key={row.id} className="border-t"><td className="p-2">{row.label}</td><td className="p-2 text-right">{Number(row.outstanding).toFixed(2)}</td><td className="p-2"><input type="number" step="0.01" min="0" className="w-full rounded border p-1 text-right" value={allocDraft[row.id] ?? ''} onChange={(e) => setAllocDraft((prev) => ({ ...prev, [row.id]: e.target.value }))} /></td></tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="mt-2 flex items-center justify-between text-xs"><span>Allocated: {allocationTotal.toFixed(2)}</span><span>Difference: {(amount - allocationTotal).toFixed(2)}</span></div>
                                <FieldErrorText message={fieldErrors.allocations} />
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            <Button type="submit" disabled={loading}>{loading ? 'Saving...' : editingPdcId ? 'Update Allocations' : 'Register PDC'}</Button>
                            {editingPdcId && <Button type="button" variant="outline" onClick={() => setEditingPdcId(null)}>Cancel Edit</Button>}
                        </div>
                    </form>
                </CardContent>
            </Card>
            <Card>
                <CardHeader><CardTitle>PDC List</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <select className="rounded border p-2 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="">All Status</option>
                            {Object.entries(STATUS_LABELS).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
                        </select>
                        <select className="rounded border p-2 text-sm" value={allocationBadgeFilter} onChange={(e) => setAllocationBadgeFilter(e.target.value as 'ALL' | AllocationBadge)}>
                            <option value="ALL">All Allocation Badges</option>
                            {Object.entries(ALLOCATION_BADGE_LABELS).map(([value, label]) => (<option key={value} value={value}>{label}</option>))}
                        </select>
                        <label className="flex items-center gap-2 rounded border p-2 text-sm">
                            <input type="checkbox" checked={appliedOnly} onChange={(e) => setAppliedOnly(e.target.checked)} />
                            Applied &gt; 0
                        </label>
                        <label className="flex items-center gap-2 rounded border p-2 text-sm">
                            <input type="checkbox" checked={unappliedOnly} onChange={(e) => setUnappliedOnly(e.target.checked)} />
                            Unapplied &gt; 0
                        </label>
                        <Button variant="outline" size="sm" onClick={loadRows} disabled={listLoading}>{listLoading ? 'Refreshing...' : 'Refresh'}</Button>
                    </div>
                    {(allocationLoading || (allocatingPdc && allocationReceipt)) && (
                        <div className="rounded border p-3">
                            {allocationLoading && <p className="text-sm text-muted-foreground">Loading prepayment allocation details...</p>}
                            {allocatingPdc && allocationReceipt && (
                                <div className="space-y-2">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <p className="text-sm font-medium">
                                            Allocate Cleared Prepayment: PDC #{allocatingPdc.pdc_id} -&gt; Receipt #{allocationReceipt.receipt_id}
                                        </p>
                                        <Button type="button" size="sm" variant="outline" onClick={resetPrepaymentAllocation}>Close</Button>
                                    </div>
                                    <div className="grid grid-cols-1 gap-2 text-xs md:grid-cols-3">
                                        <div className="rounded border p-2">Customer: <strong>{allocatingPdc.party_name || allocatingPdc.party_id}</strong></div>
                                        <div className="rounded border p-2">Receipt Amount: <strong>{Number(allocationReceipt.amount || 0).toFixed(2)}</strong></div>
                                        <div className="rounded border p-2">Unapplied Balance: <strong>{Number(allocationReceipt.unapplied_amount || 0).toFixed(2)}</strong></div>
                                    </div>
                                    {allocationOpenInvoices.length === 0 ? (
                                        <p className="text-xs text-muted-foreground">No open invoices found for this customer.</p>
                                    ) : (
                                        <div className="max-h-52 overflow-auto rounded border">
                                            <table className="w-full text-sm">
                                                <thead className="bg-muted/30"><tr><th className="p-2 text-left">Invoice</th><th className="p-2 text-right">Outstanding</th><th className="p-2 text-right">Apply</th></tr></thead>
                                                <tbody>
                                                    {allocationOpenInvoices.map((invoice) => (
                                                        <tr key={invoice.invoice_id} className="border-t">
                                                            <td className="p-2">{invoice.invoice_no}</td>
                                                            <td className="p-2 text-right">{Number(invoice.outstanding_amount || 0).toFixed(2)}</td>
                                                            <td className="p-2">
                                                                <input
                                                                    type="number"
                                                                    step="0.01"
                                                                    min="0"
                                                                    className="w-full rounded border p-1 text-right"
                                                                    value={allocationDraft[invoice.invoice_id] ?? ''}
                                                                    onChange={(e) => setAllocationDraft((prev) => ({ ...prev, [invoice.invoice_id]: e.target.value }))}
                                                                />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                    <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                                        <span>Allocated Now: {prepaymentAllocationTotal.toFixed(2)}</span>
                                        <span>Balance After Allocation: {(Number(allocationReceipt.unapplied_amount || 0) - prepaymentAllocationTotal).toFixed(2)}</span>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <Button type="button" size="sm" variant="outline" onClick={autoAllocatePrepayment} disabled={allocationSaving || Number(allocationReceipt.unapplied_amount || 0) <= 0.01}>Auto Allocate</Button>
                                        <Button type="button" size="sm" onClick={submitPrepaymentAllocation} disabled={allocationSaving || prepaymentAllocationLines.length === 0 || Number(allocationReceipt.unapplied_amount || 0) <= 0.01}>
                                            {allocationSaving ? 'Allocating...' : 'Allocate to Invoice'}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <div className="overflow-auto rounded border">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/40"><tr><th className="p-2 text-left">PDC</th><th className="p-2 text-left">Party</th><th className="p-2 text-left">Mode</th><th className="p-2 text-right">Amount</th><th className="p-2 text-right">Applied</th><th className="p-2 text-right">Unapplied</th><th className="p-2 text-left">Allocation</th><th className="p-2 text-left">Due</th><th className="p-2 text-left">Status</th><th className="p-2 text-left">Actions</th></tr></thead>
                            <tbody>
                                {visibleRows.length === 0 ? <tr><td colSpan={10} className="p-3 text-center text-muted-foreground">{listLoading ? 'Loading...' : 'No records.'}</td></tr> : visibleRows.map((row) => (
                                    <tr key={row.pdc_id} className="border-t">
                                        {(() => {
                                            const metric = getRowReceiptMetric(row);
                                            const fullyAllocated = !!metric && metric.unapplied <= 0.01;
                                            return (
                                                <>
                                        <td className="p-2">#{row.pdc_id} {row.instrument_no}</td>
                                        <td className="p-2">{row.party_name || row.party_id}</td>
                                        <td className="p-2">{MODE_LABELS[row.mode] || row.mode}</td>
                                        <td className="p-2 text-right">{Number(row.amount || 0).toFixed(2)}</td>
                                        <td className="p-2 text-right">{metric ? metric.applied.toFixed(2) : '-'}</td>
                                        <td className="p-2 text-right">{metric ? metric.unapplied.toFixed(2) : '-'}</td>
                                        <td className="p-2">
                                            {metric ? (
                                                <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${allocationBadgeClasses(metric.badge)}`}>
                                                    {ALLOCATION_BADGE_LABELS[metric.badge]}
                                                </span>
                                            ) : (
                                                <span className="text-xs text-muted-foreground">-</span>
                                            )}
                                        </td>
                                        <td className="p-2">{String(row.due_date || '').slice(0, 10)}</td>
                                        <td className="p-2">{STATUS_LABELS[row.status_id] || row.status_name}</td>
                                        <td className="p-2">
                                            <div className="flex flex-wrap gap-1">
                                                {[1].includes(row.status_id) && <Button size="sm" variant="outline" onClick={() => runAction(row.pdc_id, 'deposit')} disabled={!!actionBusy[row.pdc_id]}>{actionBusy[row.pdc_id] === 'deposit' ? '...' : 'Deposit'}</Button>}
                                                {[1, 2].includes(row.status_id) && <Button size="sm" variant="outline" onClick={() => openClearDialog(row)} disabled={!!actionBusy[row.pdc_id]}>{actionBusy[row.pdc_id] === 'clear' ? '...' : 'Clear'}</Button>}
                                                {[1, 2].includes(row.status_id) && <Button size="sm" variant="outline" onClick={() => runAction(row.pdc_id, 'bounce')} disabled={!!actionBusy[row.pdc_id]}>{actionBusy[row.pdc_id] === 'bounce' ? '...' : 'Bounce'}</Button>}
                                                {[1, 2].includes(row.status_id) && <Button size="sm" variant="outline" onClick={() => runAction(row.pdc_id, 'cancel')} disabled={!!actionBusy[row.pdc_id]}>{actionBusy[row.pdc_id] === 'cancel' ? '...' : 'Cancel'}</Button>}
                                                {isSettlementMode(side, row.mode || '') && [1, 2].includes(row.status_id) && <Button size="sm" variant="secondary" onClick={() => loadForAllocationEdit(row)}>Edit Alloc</Button>}
                                                {isClearedCustomerPrepayment(row) && Number(row.cleared_receipt_id || 0) > 0 && <Button size="sm" variant="secondary" onClick={() => beginPrepaymentAllocation(row)} disabled={allocationLoading || allocationSaving || fullyAllocated}>{fullyAllocated ? 'Allocated' : (allocatingPdc?.pdc_id === row.pdc_id ? 'Allocating' : 'Allocate')}</Button>}
                                            </div>
                                            {isClearedCustomerPrepayment(row) && Number(row.cleared_receipt_id || 0) > 0 && <div className="mt-1 text-xs text-muted-foreground">Receipt #{Number(row.cleared_receipt_id || 0)}</div>}
                                        </td>
                                                </>
                                            );
                                        })()}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
            <Dialog
                open={!!clearDialogPdc}
                onOpenChange={(open) => {
                    if (!open) {
                        setClearDialogPdc(null);
                        setClearCashAccountId('');
                        setClearanceDate('');
                    }
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Clear PDC</DialogTitle>
                        <DialogDescription>
                            Select the exact Cash/Bank account where this PDC will be posted before clearing.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 gap-2 rounded border bg-muted/20 p-3 text-sm md:grid-cols-2">
                            <div>PDC: <strong>#{clearDialogPdc?.pdc_id} {clearDialogPdc?.instrument_no}</strong></div>
                            <div>Amount: <strong>{Number(clearDialogPdc?.amount || 0).toFixed(2)}</strong></div>
                            <div>Party: <strong>{clearDialogPdc?.party_name || clearDialogPdc?.party_id || '-'}</strong></div>
                            <div>Mode: <strong>{MODE_LABELS[String(clearDialogPdc?.mode || '')] || clearDialogPdc?.mode || '-'}</strong></div>
                        </div>
                        <label className="space-y-2 text-sm">
                            <RequiredLabel required htmlFor="clearanceDate">Clearance Date</RequiredLabel>
                            <input
                                id="clearanceDate"
                                type="date"
                                className="w-full rounded border p-2"
                                value={clearanceDate}
                                onChange={(event) => setClearanceDate(event.target.value)}
                            />
                            <p className="text-xs text-muted-foreground">
                                The receipt/payment and journal will be posted using this bank-cleared date.
                            </p>
                        </label>
                        <label className="space-y-2 text-sm">
                            <RequiredLabel required htmlFor="clearCashAccountId">Cash/Bank Account</RequiredLabel>
                            <select
                                id="clearCashAccountId"
                                className="w-full rounded border p-2"
                                value={clearCashAccountId}
                                onChange={(event) => setClearCashAccountId(event.target.value)}
                            >
                                <option value="">Select posting account</option>
                                {cashAccounts.map((account) => (
                                    <option key={account.id} value={account.id}>
                                        {account.code} - {account.name}
                                    </option>
                                ))}
                            </select>
                            <p className="text-xs text-muted-foreground">
                                Clearing will create the receipt/payment journal using this account.
                            </p>
                        </label>
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                setClearDialogPdc(null);
                                setClearCashAccountId('');
                                setClearanceDate('');
                            }}
                            disabled={clearDialogPdc ? !!actionBusy[clearDialogPdc.pdc_id] : false}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={() => void confirmClearPdc()}
                            disabled={!clearCashAccountId || !clearanceDate || (clearDialogPdc ? !!actionBusy[clearDialogPdc.pdc_id] : false)}
                        >
                            {clearDialogPdc && actionBusy[clearDialogPdc.pdc_id] === 'clear' ? 'Clearing...' : 'Clear PDC'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
